import { jsPDF, AcroFormTextField } from 'jspdf'

const AI_SCORE_DISCLAIMER = 'AI-generated scores and feedback are for reference only. The score given by the module teacher is final.'

const PAGE = {
  width: 595.28,
  height: 841.89,
  marginX: 46,
  marginTop: 46,
  marginBottom: 48,
}

const COLORS = {
  ink: '#171717',
  muted: '#5e5d59',
  border: '#dedbd2',
  paper: '#fbfaf6',
  card: '#fffdf8',
  accent: '#b75a3b',
  accentSoft: '#f0eee6',
  assistant: '#f0f6f8',
  reviewed: '#dcead8',
  blue: '#d8e8f1',
  gray: '#ece9df',
}

export function buildChatSummary(record) {
  const messages = record?.messages || []
  const studentCount = messages.filter(message => message.role === 'user').length
  const assistantCount = messages.filter(message => message.role !== 'user').length
  const modes = [...new Set(messages.map(message => message.mode || message.tool || 'chat').filter(Boolean))]
  const modeText = modes.length ? ` Interaction modes include ${modes.join(', ')}.` : ''
  const base = messages.length
    ? `The student exchanged ${messages.length} messages with the assistant (${studentCount} student messages and ${assistantCount} assistant responses).${modeText}`
    : 'No chatbot messages were saved for this writing record.'
  const analysisSummary = record?.latest_analysis?.summary
  return analysisSummary ? `${base} Latest AI analysis: ${analysisSummary}` : base
}

export async function saveWritingReportPdf(record, options = {}) {
  const { pdf, filename } = generateWritingReportPdf(record, options)
  const blob = pdf.output('blob')
  if (window.showSaveFilePicker) {
    const handle = await window.showSaveFilePicker({
      suggestedName: filename,
      types: [
        {
          description: 'PDF document',
          accept: { 'application/pdf': ['.pdf'] },
        },
      ],
    })
    const writable = await handle.createWritable()
    await writable.write(blob)
    await writable.close()
    return { filename, pickedLocation: true }
  }
  pdf.save(filename)
  return { filename, pickedLocation: false }
}

export function generateWritingReportPdf(record, { audience = 'teacher' } = {}) {
  if (!record) throw new Error('No writing record is loaded.')

  const pdf = new jsPDF({ unit: 'pt', format: 'a4' })
  const state = { y: PAGE.marginTop, studentReport: audience === 'student' }
  drawPageBackground(pdf)
  if (audience === 'student') drawStudentIdentification(pdf, state)
  drawTitle(pdf, state, record)
  drawMetadata(pdf, state, record)
  drawSectionHeading(pdf, state, 'Assignment Prompt')
  drawCardText(pdf, state, record.assignment?.prompt || 'No assignment prompt available.')
  drawSectionHeading(pdf, state, 'Student Draft')
  for (const [name, content] of sectionEntries(record)) {
    drawCardText(pdf, state, content || 'No content yet.', titleCase(name))
  }
  drawSectionHeading(pdf, state, 'AI Draft Score and Feedback')
  drawCallout(pdf, state, AI_SCORE_DISCLAIMER)
  drawScoreBlock(pdf, state, record.score_result, 'No AI draft score yet.')
  drawSectionHeading(pdf, state, 'Teacher Score and Feedback')
  drawTeacherReview(pdf, state, record)
  drawSectionHeading(pdf, state, 'Chat History Analysis')
  drawAnalysisBlock(pdf, state, record.latest_analysis)
  drawSectionHeading(pdf, state, 'Chat History Summary')
  drawCallout(pdf, state, buildChatSummary(record))
  addPage(pdf, state)
  drawSectionHeading(pdf, state, 'Appendix: Full Chat History')
  drawChatHistory(pdf, state, record.messages || [])

  const filename = `${safeFileName(record.assignment?.title || 'assignment')}-${safeFileName(displayName(record.student) || 'student')}-report.pdf`
  return { pdf, filename }
}

function drawStudentIdentification(pdf, state) {
  pdf.setFont('helvetica', 'normal')
  pdf.setTextColor(COLORS.muted)
  pdf.setFontSize(10)
  pdf.text('Professional Workplace Communication:', PAGE.marginX, state.y)
  state.y += 20
  pdf.setFont('helvetica', 'bold')
  pdf.setFontSize(16)
  pdf.setTextColor(COLORS.ink)
  pdf.text('Proposal and Report Writing (LAN4103)', PAGE.marginX, state.y)
  state.y += 19
  pdf.setFont('helvetica', 'normal')
  pdf.setFontSize(10)
  pdf.text('Notional Learning Assignment (15%): Mini-research Proposal', PAGE.marginX, state.y)
  state.y += 14
  pdf.setDrawColor(COLORS.border)
  pdf.line(PAGE.marginX, state.y, PAGE.width - PAGE.marginX, state.y)
  state.y += 20
  const fieldsTop = state.y
  const halfWidth = (contentWidth() - 18) / 2
  const fields = [
    ['student_name', 'Name of Student:', 100, 0, 0, halfWidth],
    ['student_number', 'Student Number:', 40, halfWidth + 18, 0, halfWidth],
    ['programme_class', 'Programme Code/Class:', 100, 0, 49, contentWidth()],
    ['campus', 'Campus:', 80, 0, 98, contentWidth()],
  ]
  for (const [name, label, maxLength, offsetX, offsetY, width] of fields) {
    const x = PAGE.marginX + offsetX
    const y = fieldsTop + offsetY
    pdf.setFont('helvetica', 'normal')
    pdf.setFontSize(9)
    pdf.setTextColor(COLORS.muted)
    pdf.text(label, x, y)
    pdf.setDrawColor(COLORS.border)
    pdf.setFillColor('#ffffff')
    pdf.rect(x, y + 7, width, 25, 'FD')
    const field = new AcroFormTextField()
    field.fieldName = name
    field.Rect = [x + 3, y + 9, width - 6, 21]
    field.fontName = 'helvetica'
    field.fontStyle = 'normal'
    // Zero enables viewer auto-sizing; keep the printed value inside the box.
    field.fontSize = 0
    field.maxFontSize = 11
    field.maxLength = maxLength
    field.showWhenPrinted = true
    field.doNotScroll = true
    field.value = ''
    field.defaultValue = ''
    pdf.addField(field)
  }
  state.y = fieldsTop + 143
  // jsPDF 4.x omits /DA on text widgets. Supply inherited appearance and
  // standard Adobe GB1 CID font resources for Latin and Chinese form input.
  // A FontDescriptor and widths are essential: without them readers may save
  // a value without a printable appearance. Browser + Poppler roundtrip tests
  // cover this resource dictionary; no font download or personal-data upload.
  const form = pdf.internal.acroformPlugin.acroFormDictionaryRoot
  let formFontId, descriptor, descendant
  // AcroForm is serialized before fonts. Reserve references on every build,
  // including the first output() and subsequent saves of the same document.
  pdf.internal.events.subscribe('buildDocument', () => {
    descriptor = pdf.internal.newObjectDeferred()
    descendant = pdf.internal.newObjectDeferred()
    formFontId = pdf.internal.newObjectDeferred()
  })
  pdf.internal.events.subscribe('putResources', () => {
    pdf.internal.newObjectDeferredBegin(descriptor, true)
    pdf.internal.write('<< /Type /FontDescriptor /FontName /STSongStd-Light /Ascent 752 /CapHeight 737 /Descent -271 /Flags 6 /FontBBox [-25 -254 1000 880] /ItalicAngle 0 /StemV 58 >>')
    pdf.internal.write('endobj')
    pdf.internal.newObjectDeferredBegin(descendant, true)
    pdf.internal.write(`<< /Type /Font /Subtype /CIDFontType0 /BaseFont /STSong-Light /FontDescriptor ${descriptor} 0 R /CIDSystemInfo << /Registry (Adobe) /Ordering (GB1) /Supplement 0 >> /DW 1000 /W [1 [207 270 342 467 462 797 710 239 374] 10 [374 423 605 238 375 238 334 462] 18 26 462 27 28 238 29 31 605 32 [344 748 684 560 695 739 563 511 729 793 318 312 666 526 896 758 772 544 772 628 465 607 753 711 972 647 620 607 374 333 374 606 500 239 417 503 427 529 415 264 444 518 241 230 495 228 793 527 524] 81 [524 504 338 336 277 517 450 652 466 452 407 370 258 370 605]] >>`)
    pdf.internal.write('endobj')
    pdf.internal.newObjectDeferredBegin(formFontId, true)
    pdf.internal.write(`<< /Type /Font /Subtype /Type0 /BaseFont /STSong-Light /Encoding /UniGB-UCS2-H /DescendantFonts [${descendant} 0 R] >>`)
    pdf.internal.write('endobj')
  })
  form.DA = '/StudentFormFont 0 Tf 0 g'
  Object.defineProperty(form, 'DR', {
    configurable: false,
    get: () => `<< /Font << /StudentFormFont ${formFontId} 0 R >> >>`,
  })
  pdf.setFont('helvetica', 'italic')
  pdf.setFontSize(9)
  pdf.setTextColor(COLORS.muted)
  pdf.text('Please enter your information after downloading the PDF from the AI Writing System.', PAGE.marginX, state.y + 5)
  state.y += 31
}

function drawPageBackground(pdf) {
  pdf.setFillColor(COLORS.paper)
  pdf.rect(0, 0, PAGE.width, PAGE.height, 'F')
}

function addPage(pdf, state) {
  pdf.addPage()
  drawPageBackground(pdf)
  state.y = PAGE.marginTop
}

function ensureSpace(pdf, state, height) {
  if (state.y + height <= PAGE.height - PAGE.marginBottom) return
  addPage(pdf, state)
}

function contentWidth() {
  return PAGE.width - PAGE.marginX * 2
}

function drawTitle(pdf, state, record) {
  if (state.studentReport) {
    pdf.setFont('helvetica', 'bold')
    pdf.setFontSize(10)
    pdf.setTextColor(COLORS.ink)
    pdf.text('WRITING PRACTICE REPORT', PAGE.marginX, state.y)
    state.y += 18
    pdf.setFont('helvetica', 'normal')
    pdf.setFontSize(12)
    const lines = pdf.splitTextToSize(record.assignment?.title || 'Writing Practice', contentWidth() - 100)
    pdf.text(lines, PAGE.marginX, state.y)
    pdf.setFontSize(9)
    pdf.setTextColor(COLORS.muted)
    pdf.text(labelStatus(record.status), PAGE.width - PAGE.marginX, state.y, { align: 'right' })
    state.y += lines.length * 14 + 12
    return
  }
  const titleTop = state.y
  pdf.setFont('times', 'normal')
  pdf.setTextColor(COLORS.accent)
  pdf.setFontSize(10)
  pdf.text('WRITING PRACTICE REPORT', PAGE.marginX, state.y)
  state.y += state.studentReport ? 27 : 20
  pdf.setTextColor(COLORS.ink)
  pdf.setFontSize(28)
  const titleLines = pdf.splitTextToSize(record.assignment?.title || 'Writing Practice', contentWidth() - 110)
  pdf.text(titleLines, PAGE.marginX, state.y)
  pdf.setFillColor(statusColor(record.status))
  pdf.roundedRect(PAGE.width - PAGE.marginX - 88, titleTop + 8, 88, 22, 11, 11, 'F')
  pdf.setFont('helvetica', 'bold')
  pdf.setFontSize(9)
  pdf.setTextColor(statusTextColor(record.status))
  pdf.text(labelStatus(record.status), PAGE.width - PAGE.marginX - 44, titleTop + 22, { align: 'center' })
  state.y += titleLines.length * 28 + 10
  pdf.setDrawColor(COLORS.border)
  pdf.setLineWidth(1.2)
  pdf.line(PAGE.marginX, state.y, PAGE.width - PAGE.marginX, state.y)
  state.y += 14
}

function drawMetadata(pdf, state, record) {
  const assignment = record.assignment || {}
  const items = [
    [state.studentReport ? 'System account' : 'Student', displayName(record.student) || '-'],
    [state.studentReport ? 'System class' : 'Class', record.student?.class_group || '-'],
    ['Teacher', displayName(assignment.created_by) || '-'],
    ['Submitted', formatDateTime(record.submitted_at) || '-'],
    ['Start', formatDateTime(assignment.start_at) || '-'],
    ['Due', formatDateTime(assignment.due_at) || '-'],
    ['AI Score', scoreLabel(record.score_result)],
    ['Teacher Score', record.teacher_score == null ? '-' : `${record.teacher_score} / 4`],
  ]
  const gap = state.studentReport ? 0 : 8
  const colWidth = (contentWidth() - gap * 3) / 4
  const rowHeight = state.studentReport ? 40 : 48
  ensureSpace(pdf, state, rowHeight * 2 + gap)
  items.forEach((item, index) => {
    const col = index % 4
    const row = Math.floor(index / 4)
    const x = PAGE.marginX + col * (colWidth + gap)
    const y = state.y + row * (rowHeight + gap)
    pdf.setFillColor(COLORS.card)
    pdf.setDrawColor(COLORS.border)
    if (state.studentReport) {
      pdf.setLineWidth(0.5)
      pdf.line(x, y, x + colWidth, y)
    } else {
      pdf.roundedRect(x, y, colWidth, rowHeight, 6, 6, 'FD')
    }
    pdf.setFont('helvetica', 'bold')
    pdf.setFontSize(8)
    pdf.setTextColor(COLORS.muted)
    pdf.text(item[0].toUpperCase(), x + 8, y + 16)
    pdf.setFontSize(10)
    pdf.setTextColor(COLORS.ink)
    pdf.text(pdf.splitTextToSize(String(item[1]), colWidth - 16), x + 8, y + 32)
  })
  state.y += rowHeight * 2 + gap + (state.studentReport ? 18 : 8)
}

function drawSectionHeading(pdf, state, title) {
  ensureSpace(pdf, state, state.studentReport ? 100 : 42)
  state.y += state.studentReport ? (state.y === PAGE.marginTop ? 10 : 20) : 8
  pdf.setFont(state.studentReport ? 'helvetica' : 'times', state.studentReport ? 'bold' : 'normal')
  pdf.setFontSize(state.studentReport ? 14 : 18)
  pdf.setTextColor(COLORS.ink)
  pdf.text(title, PAGE.marginX, state.y)
  state.y += 10
  pdf.setDrawColor(COLORS.border)
  if (state.studentReport) pdf.setLineWidth(0.5)
  pdf.line(PAGE.marginX, state.y, PAGE.width - PAGE.marginX, state.y)
  state.y += 12
}

function drawCardText(pdf, state, text, heading = '') {
  drawPagedBox(pdf, state, {
    text,
    heading,
    x: PAGE.marginX,
    width: contentWidth(),
    fill: COLORS.card,
    stroke: COLORS.border,
    textColor: COLORS.ink,
    headingColor: state.studentReport ? COLORS.ink : COLORS.accent,
    fontSize: 10,
    lineHeight: 15,
    padding: state.studentReport ? 12 : 11,
  })
}

function drawScoreBlock(pdf, state, scoreResult, emptyText) {
  if (!scoreResult || !Object.keys(scoreResult).length) {
    drawCardText(pdf, state, emptyText)
    return
  }
  const lines = [
    scoreResult.summary || '',
    ...(scoreResult.dimensions || []).map(item => `${item.name}: ${item.comment || ''}`),
  ].filter(Boolean)
  drawCardText(pdf, state, lines.join('\n\n'), scoreLabel(scoreResult))
}

function drawTeacherReview(pdf, state, record) {
  const score = record.teacher_score == null ? (state.studentReport ? '' : '-') : `${record.teacher_score} / 4`
  drawCardText(pdf, state, record.teacher_feedback || 'Not reviewed yet.', score)
}

function drawAnalysisBlock(pdf, state, analysis) {
  if (!analysis) {
    drawCardText(pdf, state, 'No chat analysis yet.')
    return
  }
  const dimensions = analysis.dimensions || []
  const text = [
    ...(dimensions.map(item => `${item.name} (${Number(item.score || 1)} / 5): ${item.comment || 'No comment yet.'}`)),
    analysis.summary ? `Overall: ${analysis.summary}` : '',
  ].filter(Boolean).join('\n\n')
  drawCardText(pdf, state, text || 'No chat analysis yet.')
}

function drawCallout(pdf, state, text) {
  drawPagedBox(pdf, state, {
    text,
    x: PAGE.marginX,
    width: contentWidth(),
    fill: COLORS.accentSoft,
    stroke: state.studentReport ? COLORS.border : COLORS.accent,
    textColor: COLORS.ink,
    fontSize: 10,
    lineHeight: 15,
    padding: 12,
  })
}

function drawChatHistory(pdf, state, messages) {
  if (!messages.length) {
    drawCardText(pdf, state, 'No chatbot messages yet.')
    return
  }
  messages.forEach(message => drawChatMessage(pdf, state, message))
}

function drawChatMessage(pdf, state, message) {
  const isUser = message.role === 'user'
  const isTool = isToolMessagePdf(message)
  const bubbleWidth = isTool ? contentWidth() * 0.92 : contentWidth() * 0.78
  const x = isUser ? PAGE.width - PAGE.marginX - bubbleWidth : PAGE.marginX
  const label = `${isUser ? 'Student' : 'Assistant'}${message.created_at ? ` - ${formatDateTime(message.created_at)}` : ''}`
  if (isTool) {
    drawToolMessage(pdf, state, message, x, bubbleWidth, label)
    return
  }
  drawPagedBox(pdf, state, {
    text: message.content || 'Empty message.',
    heading: label.toUpperCase(),
    x,
    width: bubbleWidth,
    fill: isUser ? COLORS.accent : COLORS.assistant,
    stroke: isUser ? COLORS.accent : COLORS.border,
    textColor: isUser ? COLORS.paper : COLORS.ink,
    headingColor: isUser ? COLORS.paper : COLORS.accent,
    fontSize: 10,
    lineHeight: 14,
    padding: 12,
    radius: 8,
  })
}

function drawToolMessage(pdf, state, message, x, width, label) {
  const lines = [
    { text: label.toUpperCase(), kind: 'heading' },
    { text: toolMessageLabelPdf(message).toUpperCase(), kind: 'toolLabel' },
  ]
  if (message.targetSentence) {
    lines.push({ text: 'Selected text', kind: 'caption' })
    splitPdfLines(pdf, message.targetSentence, width - 38).forEach(text => lines.push({ text, kind: 'quote' }))
  }
  splitPdfLines(pdf, toolMessageNotePdf(message), width - 24).forEach(text => lines.push({ text, kind: 'note' }))
  if (message.tool === 'selected chat' && message.content) {
    lines.push({ text: 'Student request', kind: 'caption' })
    splitPdfLines(pdf, message.content, width - 24).forEach(text => lines.push({ text, kind: 'body' }))
  }
  drawPagedLines(pdf, state, {
    lines,
    x,
    width,
    fill: COLORS.card,
    stroke: COLORS.border,
    padding: 12,
    lineHeight: 14,
    radius: 8,
    drawLine: (line, cursor) => drawToolLine(pdf, line, cursor, x, width),
    lineHeightFor: line => line.kind === 'caption' ? 11 : 14,
  })
}

function drawToolLine(pdf, line, cursor, x, width) {
  if (line.kind === 'heading') {
    pdf.setFont('helvetica', 'bold')
    pdf.setFontSize(8)
    pdf.setTextColor(COLORS.accent)
    pdf.text(line.text, x + 12, cursor)
    return
  }
  if (line.kind === 'toolLabel') {
    pdf.setFont('helvetica', 'bold')
    pdf.setFontSize(8)
    pdf.setTextColor(COLORS.accent)
    pdf.text(line.text, x + 12, cursor)
    return
  }
  if (line.kind === 'caption') {
    pdf.setFont('helvetica', 'bold')
    pdf.setFontSize(7)
    pdf.setTextColor(COLORS.muted)
    pdf.text(line.text.toUpperCase(), x + 12, cursor)
    return
  }
  if (line.kind === 'quote') {
    pdf.setFillColor(COLORS.accentSoft)
    pdf.setDrawColor(COLORS.accent)
    pdf.setLineWidth(2)
    pdf.line(x + 15, cursor - 10, x + 15, cursor + 4)
    pdf.setFont('helvetica', 'normal')
    pdf.setFontSize(9)
    pdf.setTextColor(COLORS.muted)
    pdf.text(line.text, x + 24, cursor)
    return
  }
  pdf.setFont('helvetica', 'normal')
  pdf.setFontSize(9)
  pdf.setTextColor(line.kind === 'note' ? COLORS.muted : COLORS.ink)
  pdf.text(line.text, x + 12, cursor)
}

function drawPagedBox(pdf, state, options) {
  const innerWidth = options.width - options.padding * 2
  // Measure in the font actually used for body text, not the preceding title.
  if (state.studentReport) {
    pdf.setFont('helvetica', 'normal')
    pdf.setFontSize(options.fontSize)
  }
  const contentLines = textLineObjects(pdf, options.text, innerWidth)
  const lines = options.heading
    ? [{ text: options.heading, kind: 'heading' }, ...contentLines]
    : contentLines
  drawPagedLines(pdf, state, {
    ...options,
    lines,
    drawLine: (line, cursor) => {
      pdf.setFont(line.kind === 'heading' && !state.studentReport ? 'times' : 'helvetica', line.kind === 'heading' && state.studentReport ? 'bold' : 'normal')
      pdf.setFontSize(line.kind === 'heading' ? (state.studentReport ? 11 : 13) : options.fontSize)
      pdf.setTextColor(line.kind === 'heading' ? options.headingColor : options.textColor)
      pdf.text(line.text, options.x + options.padding, cursor)
    },
    lineHeightFor: line => {
      if (line.kind === 'spacer') return Math.round(options.lineHeight * 0.55)
      if (line.kind === 'heading') return 18
      return options.lineHeight
    },
  })
}

function drawPagedLines(pdf, state, options) {
  const radius = options.radius || (state.studentReport ? 3 : 6)
  let index = 0
  while (index < options.lines.length) {
    if (state.y > PAGE.height - PAGE.marginBottom - 50) addPage(pdf, state)
    const startY = state.y
    const bottomLimit = PAGE.height - PAGE.marginBottom
    const available = bottomLimit - startY
    const chunk = []
    let usedHeight = options.padding * 2
    while (index < options.lines.length) {
      const line = options.lines[index]
      const nextHeight = options.lineHeightFor(line)
      if (chunk.length && usedHeight + nextHeight > available) break
      chunk.push(line)
      usedHeight += nextHeight
      index += 1
      if (usedHeight > available) break
    }
    const boxHeight = Math.max(38, Math.min(usedHeight, bottomLimit - startY))
    pdf.setFillColor(options.fill)
    pdf.setDrawColor(options.stroke)
    pdf.setLineWidth(state.studentReport ? 0.5 : 1)
    pdf.roundedRect(options.x, startY, options.width, boxHeight, radius, radius, 'FD')
    // The baseline sits inside a line's height, leaving balanced top/bottom
    // padding. The old +3 placed glyphs almost against the top border.
    let cursor = startY + options.padding + (state.studentReport ? 10 : 3)
    chunk.forEach(line => {
      if (line.kind === 'spacer') {
        cursor += options.lineHeightFor(line)
        return
      }
      options.drawLine(line, cursor)
      cursor += options.lineHeightFor(line)
    })
    state.y = startY + boxHeight + 8
    if (index < options.lines.length) addPage(pdf, state)
  }
}

function drawWrappedText(pdf, state, text, x, y, width, options = {}) {
  const lineHeight = options.lineHeight || 14
  pdf.setFont('helvetica', options.bold ? 'bold' : 'normal')
  pdf.setFontSize(options.fontSize || 10)
  pdf.setTextColor(options.color || COLORS.ink)
  const paragraphs = String(text || '').split(/\n+/)
  let cursorY = y
  paragraphs.forEach((paragraph, paragraphIndex) => {
    const lines = pdf.splitTextToSize(paragraph || ' ', width)
    lines.forEach(line => {
      if (cursorY + lineHeight > PAGE.height - PAGE.marginBottom) {
        addPage(pdf, state)
        cursorY = state.y
      }
      pdf.text(line, x, cursorY)
      cursorY += lineHeight
    })
    if (paragraphIndex < paragraphs.length - 1) cursorY += 5
  })
  state.y = cursorY
}

function textLines(pdf, text, width) {
  return String(text || ' ').split(/\n+/).flatMap(paragraph => pdf.splitTextToSize(paragraph || ' ', width))
}

function textLineObjects(pdf, text, width) {
  const paragraphs = String(text || ' ').split(/\n+/)
  const lines = []
  paragraphs.forEach((paragraph, paragraphIndex) => {
    if (paragraphIndex > 0) lines.push({ text: '', kind: 'spacer' })
    splitPdfLines(pdf, paragraph || ' ', width).forEach(line => lines.push({ text: line, kind: 'body' }))
  })
  return lines.length ? lines : [{ text: ' ', kind: 'body' }]
}

function splitPdfLines(pdf, text, width) {
  return pdf.splitTextToSize(String(text || ' '), width)
}

function sectionEntries(record) {
  const sections = record?.draft_sections || {}
  const preferred = ['background', 'objective', 'methodology', 'references']
  const entries = preferred
    .filter(key => Object.prototype.hasOwnProperty.call(sections, key))
    .map(key => [key, stripHtml(sections[key])])
  const extras = Object.entries(sections)
    .filter(([key]) => !preferred.includes(key))
    .map(([key, value]) => [key, stripHtml(value)])
  return [...entries, ...extras]
}

function isToolMessagePdf(message) {
  return ['polish', 'references', 'feedback'].includes(message.mode) || Boolean(message.tool || message.targetSentence)
}

function toolMessageLabelPdf(message) {
  return message.tool || (message.mode === 'references' ? 'find reference' : message.mode === 'polish' ? 'improve writing' : 'general feedback')
}

function toolMessageNotePdf(message) {
  if (message.mode === 'references') return message.targetSentence ? 'Finding references for this selected text.' : 'Finding references for the full draft.'
  if (message.mode === 'polish') return 'Improving the selected writing.'
  if (message.mode === 'feedback') return 'Giving feedback on the full draft.'
  if (message.tool === 'selected chat') return message.content || 'Asking about the selected text.'
  return message.content || 'Tool request.'
}

function scoreLabel(scoreResult) {
  if (!scoreResult || scoreResult.score == null) return '-'
  return `${scoreResult.score} / 4`
}

function displayName(user) {
  return user?.display_name || user?.username || ''
}

function formatDateTime(value) {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  return new Intl.DateTimeFormat('en', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).format(date)
}

function labelStatus(status) {
  return {
    not_started: 'Not Started',
    in_progress: 'In Progress',
    submitted: 'Submitted',
    reviewed: 'Reviewed',
  }[status] || titleCase(status || '')
}

function statusColor(status) {
  return {
    in_progress: COLORS.blue,
    submitted: '#efe2c7',
    reviewed: COLORS.reviewed,
  }[status] || COLORS.gray
}

function statusTextColor(status) {
  return {
    in_progress: '#27536d',
    submitted: '#76511c',
    reviewed: '#335f31',
  }[status] || '#6a655d'
}

function safeFileName(value) {
  return String(value || 'report')
    .trim()
    .replace(/[^a-z0-9]+/gi, '-')
    .replace(/^-+|-+$/g, '')
    .toLowerCase() || 'report'
}

function titleCase(value = '') {
  return String(value)
    .replace(/[_-]+/g, ' ')
    .replace(/\w\S*/g, word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
}

function stripHtml(value = '') {
  return String(value)
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#039;/g, "'")
}
